import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { assessmentsAPI, sessionsAPI } from '../utils/api';
import ChallengeLab from './ChallengeLab';
import AdLabAccess from './candidate/AdLabAccess';
import './CandidateDashboard.css';

/**
 * AssessmentLab Component
 *
 * Multi-challenge assessment flow:
 * 1. Starts/resumes assessment, fetches all challenge sessions
 * 2. Shows challenge picker if multiple challenges
 * 3. Loads selected ChallengeLab
 */
function AssessmentLab() {
  const { id: assessmentId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [assessment, setAssessment] = useState(null);
  const [allSessions, setAllSessions] = useState([]);
  const [activeSession, setActiveSession] = useState(null);
  const [showingLab, setShowingLab] = useState(false);

  useEffect(() => {
    loadAssessment();
  }, [assessmentId]);

  const loadAssessment = async () => {
    try {
      setLoading(true);
      setError('');

      // For red-team assessments, fetch assessment details without starting a session
      // (there are no challenge sessions — the lab is provisioned via AdLabAccess)
      const detailCheck = await assessmentsAPI.getById(assessmentId);
      const found = detailCheck.data.assessment || detailCheck.data;
      if (found && ['red-team', 'ad-lab'].includes(found.challenges?.selectionMode)) {
        setAssessment(found);
        setLoading(false);
        return;
      }

      // Start/resume the assessment — this creates sessions if they don't exist
      const response = await assessmentsAPI.start(assessmentId);
      const assessmentData = response.data.assessment;
      setAssessment(assessmentData);

      // Re-fetch this assessment now that start() has created its sessions.
      // GET /:id carries challengeSessions; scanning the paginated list for it
      // missed any assessment beyond the first page.
      const detailRes = await assessmentsAPI.getById(assessmentId);
      const fullAssessment = detailRes.data.assessment || detailRes.data;

      if (fullAssessment?.challengeSessions?.length > 0) {
        setAllSessions(fullAssessment.challengeSessions);

        // If only 1 challenge, go directly to lab
        if (fullAssessment.challengeSessions.length === 1) {
          setActiveSession(response.data.session);
          setShowingLab(true);
        }
      } else {
        // Fallback: single session from start response
        setAllSessions([{
          sessionId: response.data.session.sessionId,
          status: response.data.session.status,
          sessionUrl: response.data.session.sessionUrl,
          challenge: response.data.session.challengeId ? {
            title: response.data.session.challengeId.title || 'Challenge',
            difficulty: response.data.session.challengeId.difficulty,
            category: response.data.session.challengeId.category,
            totalPoints: response.data.session.challengeId.totalPoints
          } : null
        }]);
        setActiveSession(response.data.session);
        setShowingLab(true);
      }

      setLoading(false);
    } catch (error) {
      console.error('[AssessmentLab] Failed to start assessment:', error);

      // Extract error string — backend may return { error: "string" } or { error: { message, status } }
      const raw = error.response?.data?.error;
      const errStr = typeof raw === 'string' ? raw
                   : typeof raw === 'object' && raw?.message ? raw.message
                   : error.response?.data?.message || '';

      if (errStr.includes('status: completed')) {
        navigate(`/assessment/${assessmentId}/results`);
        return;
      }

      let errorMessage = 'Failed to start assessment. ';
      errorMessage += errStr || 'Please try again or contact support.';

      setError(errorMessage);
      setLoading(false);
    }
  };

  const openLab = async (challengeSession) => {
    try {
      setLoading(true);

      // If session is paused or container is terminated, resume it first
      if (challengeSession.status === 'paused') {
        console.log('[AssessmentLab] Resuming paused session:', challengeSession.sessionId);
        await sessionsAPI.resume(challengeSession.sessionId);
      }

      // Get the full session object
      const res = await sessionsAPI.get(challengeSession.sessionId);
      setActiveSession(res.data.session || res.data);
      setShowingLab(true);
      setLoading(false);
    } catch (err) {
      console.error('[AssessmentLab] Failed to load session:', err);
      setLoading(false);
      // Fallback: open with what we have, ChallengeLab will handle resume
      setActiveSession(challengeSession);
      setShowingLab(true);
    }
  };

  const handleBackToList = async () => {
    // Pause current session if async
    if (activeSession?.sessionId && assessment?.mode === 'async') {
      try {
        await sessionsAPI.pause(activeSession.sessionId);
      } catch (e) { /* ignore */ }
    }
    setShowingLab(false);
    setActiveSession(null);
    // Refresh sessions to get updated statuses
    loadAssessment();
  };

  const handleSessionEnd = async () => {
    if (activeSession?.sessionId && assessment?.mode === 'async') {
      try {
        await sessionsAPI.pause(activeSession.sessionId);
      } catch (e) { /* ignore */ }
    }
    navigate('/candidate');
  };

  // Loading state
  if (loading) {
    return (
      <div className="dashboard">
        <div className="loading-state">
          <div className="cyber-spinner"></div>
          <p>Loading your assessment...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="dashboard">
        <div className="error-container">
          <div className="notification error-notification">
            <span className="notification-message">{error}</span>
          </div>
          <button onClick={() => navigate('/candidate')} className="btn btn-primary" style={{ marginTop: '20px' }}>
            Back to Dashboard
          </button>
        </div>
      </div>
    );
  }

  // Lab view — single challenge active
  if (showingLab && activeSession) {
    // Check if lab is unavailable
    if (activeSession.status === 'unavailable') {
      return (
        <div style={{ maxWidth: '600px', margin: '100px auto', padding: '2rem', textAlign: 'center' }}>
          <h2>Lab Temporarily Unavailable</h2>
          <p style={{ color: '#6c757d', margin: '1rem 0' }}>
            {activeSession.unavailabilityReason === 'docker_image_missing'
              ? 'The Docker image for this challenge is not available.'
              : 'Failed to create the lab environment.'}
          </p>
          <button onClick={handleBackToList} className="btn btn-primary">Back to Challenges</button>
        </div>
      );
    }

    return (
      <div>
        {allSessions.length > 1 && (
          <div style={{
            background: '#f8f9fa',
            padding: '0.5rem 1.5rem',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            borderBottom: '1px solid #e9ecef',
            fontSize: '0.85rem'
          }}>
            <button
              onClick={handleBackToList}
              style={{
                background: 'none', border: 'none', color: '#007bff', cursor: 'pointer',
                display: 'flex', alignItems: 'center', gap: '0.3rem', fontSize: '0.85rem'
              }}
            >
              <span>&larr;</span> All Challenges ({allSessions.length})
            </button>
            <span style={{ color: '#6c757d' }}>
              {allSessions.filter(s => s.status === 'completed').length} / {allSessions.length} completed
            </span>
          </div>
        )}
        <ChallengeLab
          session={activeSession}
          interview={assessment}
          user={user}
          onSessionEnd={allSessions.length > 1 ? handleBackToList : handleSessionEnd}
          assessmentId={assessmentId}
        />
      </div>
    );
  }

  // Challenge picker view — show all challenges
  const completed = allSessions.filter(s => s.status === 'completed').length;
  const total = allSessions.length;
  const isRedTeamLab = assessment?.adLab || ['red-team', 'ad-lab'].includes(assessment?.challenges?.selectionMode);

  return (
    <div style={{ maxWidth: '800px', margin: '2rem auto', padding: '0 1.5rem' }}>
      {/* Header */}
      <div style={{ marginBottom: '2rem' }}>
        <button
          onClick={() => navigate('/candidate')}
          style={{
            background: 'none', border: 'none', color: '#007bff', cursor: 'pointer',
            fontSize: '0.9rem', padding: 0, marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: '0.3rem'
          }}
        >
          <span>&larr;</span> Back to Dashboard
        </button>
        <h1 style={{ fontSize: '1.5rem', fontWeight: 700, marginBottom: '0.25rem' }}>
          {assessment?.name || 'Assessment'}
        </h1>
        {!isRedTeamLab && (
          <>
            <p style={{ color: '#6c757d', fontSize: '0.9rem' }}>
              {assessment?.position} &bull; {total} challenge{total !== 1 ? 's' : ''} &bull; {completed} completed
            </p>
            {/* Progress bar */}
            <div style={{
              height: '6px', background: '#e9ecef', borderRadius: '3px', marginTop: '1rem', overflow: 'hidden'
            }}>
              <div style={{
                height: '100%',
                width: `${total > 0 ? (completed / total) * 100 : 0}%`,
                background: completed === total && total > 0 ? '#28a745' : '#007bff',
                borderRadius: '3px',
                transition: 'width 0.3s ease'
              }} />
            </div>
          </>
        )}
      </div>

      {/* Red Team Lab Access — shown when assessment uses red-team or ad-lab mode */}
      {isRedTeamLab && (
        <AdLabAccess assessmentId={assessmentId} candidateId={user?._id} />
      )}

      {/* Challenge Cards */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
        {allSessions.map((cs, idx) => {
          const isCompleted = cs.status === 'completed';
          const isActive = cs.status === 'active' || cs.status === 'created' || cs.status === 'paused';

          return (
            <div
              key={cs.sessionId || idx}
              onClick={() => isActive && openLab(cs)}
              style={{
                display: 'flex',
                alignItems: 'center',
                padding: '1.25rem',
                background: isCompleted ? '#f0fdf4' : '#fff',
                borderRadius: '10px',
                border: `1px solid ${isCompleted ? '#bbf7d0' : '#e9ecef'}`,
                cursor: isActive ? 'pointer' : 'default',
                transition: 'box-shadow 0.2s, transform 0.2s',
                boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
                ...(isActive && { ':hover': { boxShadow: '0 4px 12px rgba(0,0,0,0.08)' } })
              }}
              onMouseEnter={e => { if (isActive) { e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.1)'; e.currentTarget.style.transform = 'translateY(-1px)'; } }}
              onMouseLeave={e => { e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,0.04)'; e.currentTarget.style.transform = 'none'; }}
            >
              {/* Number */}
              <div style={{
                width: '36px', height: '36px', borderRadius: '50%',
                background: isCompleted ? '#22c55e' : isActive ? '#3b82f6' : '#d1d5db',
                color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontWeight: 700, fontSize: '0.9rem', flexShrink: 0, marginRight: '1rem'
              }}>
                {isCompleted ? '✓' : idx + 1}
              </div>

              {/* Info */}
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 600, fontSize: '1rem', color: '#1f2937' }}>
                  {cs.challenge?.title || `Challenge ${idx + 1}`}
                </div>
                <div style={{ fontSize: '0.8rem', color: '#6b7280', marginTop: '0.2rem', display: 'flex', gap: '0.75rem' }}>
                  {cs.challenge?.difficulty && (
                    <span style={{
                      padding: '1px 8px', borderRadius: '4px', fontSize: '0.7rem', fontWeight: 600,
                      background: cs.challenge.difficulty === 'junior' ? '#dbeafe' : cs.challenge.difficulty === 'senior' ? '#ffedd5' : '#fce7f3',
                      color: cs.challenge.difficulty === 'junior' ? '#1e40af' : cs.challenge.difficulty === 'senior' ? '#c2410c' : '#be185d'
                    }}>{cs.challenge.difficulty}</span>
                  )}
                  {cs.challenge?.category && <span>{cs.challenge.category}</span>}
                  {cs.challenge?.totalPoints && <span>{cs.challenge.totalPoints} pts</span>}
                </div>
              </div>

              {/* Action */}
              <div style={{ flexShrink: 0, marginLeft: '1rem' }}>
                {isCompleted ? (
                  <span style={{ color: '#22c55e', fontWeight: 600, fontSize: '0.85rem' }}>Completed</span>
                ) : isActive ? (
                  <span style={{
                    padding: '6px 16px', background: '#3b82f6', color: '#fff',
                    borderRadius: '6px', fontWeight: 600, fontSize: '0.85rem'
                  }}>
                    {cs.status === 'paused' ? 'Resume' : 'Open Lab'}
                  </span>
                ) : (
                  <span style={{ color: '#9ca3af', fontSize: '0.85rem' }}>{cs.status}</span>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Complete assessment button */}
      {completed === total && total > 0 && (
        <div style={{ textAlign: 'center', marginTop: '2rem' }}>
          <button
            onClick={() => navigate(`/assessment/${assessmentId}/results`)}
            className="btn btn-primary"
            style={{ padding: '0.75rem 2rem', fontSize: '1rem' }}
          >
            View Results
          </button>
        </div>
      )}
    </div>
  );
}

export default AssessmentLab;
